import React, { useState } from 'react';
import { searchTracks } from './spotifyAPI';
import { doc, updateDoc, arrayUnion } from 'firebase/firestore';
import { db } from './firebase';
import './SongSearch.css';

function SongSearch({ playlist, onClose, onSongAdded, replacementMode = false }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState('');
  const [selectedTrack, setSelectedTrack] = useState(null);
  const [playerName, setPlayerName] = useState('');
  const [playerNumber, setPlayerNumber] = useState('');
  const [useAI, setUseAI] = useState(false);
  const [startTime, setStartTime] = useState('0');
  const [duration, setDuration] = useState('20');
  const [adding, setAdding] = useState(false);
  const [analyzingAI, setAnalyzingAI] = useState(false);

  const handleSearch = async (e) => {
    e.preventDefault();
    
    if (!searchQuery.trim()) return;
    
    setSearching(true);
    setError('');
    
    try {
      const results = await searchTracks(searchQuery);
      setSearchResults(results);
    } catch (err) {
      setError('Failed to search songs. Please try again.');
      console.error('Search error:', err);
    } finally {
      setSearching(false);
    }
  };

  const formatDuration = (ms) => {
    const minutes = Math.floor(ms / 60000);
    const seconds = ((ms % 60000) / 1000).toFixed(0);
    return `${minutes}:${seconds.padStart(2, '0')}`;
  };

  // Handle track selection in replacement mode
  const handleTrackSelect = (track) => {
    if (replacementMode) {
      // In replacement mode, immediately return the song data without asking for player info
      const newSongData = {
        songId: track.id,
        songName: track.name,
        artist: track.artist,
        album: track.album,
        albumArt: track.albumArt,
        duration: track.duration,
        previewUrl: track.previewUrl,
        uri: track.uri,
        // These will be filled in by handleReplaceSong in PlaylistView
        startTime: 0,
        endTime: 20,
        clipDuration: 20,
        aiSelected: false,
        aiReason: null
      };
      
      if (onSongAdded) onSongAdded(newSongData);
    } else {
      // Normal mode: show player assignment form
      setSelectedTrack(track);
    }
  };

  const handleAddToPlaylist = async (e) => {
    e.preventDefault();
    setAdding(true);
    setError('');

    try {
      let finalStartTime = parseInt(startTime);
      let aiReason = null;

      // If AI mode, get Claude's recommendation
      if (useAI) {
        setAnalyzingAI(true);
        try {
          console.log('Asking Claude for optimal clip...');
                    
          const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const response = await fetch(`${apiUrl}/api/analyze-clip`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              songName: selectedTrack.name,
              artist: selectedTrack.artist,
              duration: parseInt(duration)
            })
          });

          if (!response.ok) {
            throw new Error('Failed to analyze song');
          }

          const aiResult = await response.json();
          
          finalStartTime = aiResult.startTime;
          aiReason = aiResult.reason;
          console.log('AI recommended:', aiResult);
        } catch (aiError) {
          console.error('AI analysis failed:', aiError);
          setError('AI analysis failed. Using manual start time instead.');
          setAnalyzingAI(false);
          setAdding(false);
          return;
        } finally {
          setAnalyzingAI(false);
        }
      }

      const start = finalStartTime;
      const dur = parseInt(duration);
      const end = start + dur;

      const newSong = {
        songId: selectedTrack.id,
        songName: selectedTrack.name,
        artist: selectedTrack.artist,
        album: selectedTrack.album,
        albumArt: selectedTrack.albumArt,
        duration: selectedTrack.duration,
        previewUrl: selectedTrack.previewUrl,
        uri: selectedTrack.uri,
        explicit: selectedTrack.explicit || false,
        playerName: playerName,
        playerNumber: parseInt(playerNumber),
        startTime: start,
        endTime: end,
        clipDuration: dur,
        aiSelected: useAI,
        aiReason: aiReason,
        order: playlist.songs.length + 1,
        addedAt: new Date().toISOString()
      };

      const playlistRef = doc(db, 'playlists', playlist.id);
      await updateDoc(playlistRef, {
        songs: arrayUnion(newSong),
        updatedAt: new Date().toISOString()
      });

      console.log('Song added to playlist!');
      
      // Reset form
      setSelectedTrack(null);
      setPlayerName('');
      setPlayerNumber('');
      setStartTime('0');
      setDuration('20');
      setUseAI(false);
      
      if (onSongAdded) onSongAdded();
      
    } catch (err) {
      setError('Failed to add song. Please try again.');
      console.error('Add error:', err);
    } finally {
      setAdding(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="song-search-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{replacementMode ? 'Change Song' : `Add Song to ${playlist.name}`}</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        {!selectedTrack ? (
          <>
            <form className="search-form" onSubmit={handleSearch}>
              <input
                type="text"
                placeholder="Search for a song..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="search-input"
              />
              <button type="submit" className="search-btn" disabled={searching}>
                {searching ? 'Searching...' : 'Search'}
              </button>
            </form>

            {error && <div className="error-message">{error}</div>}

            <div className="search-results">
              {searchResults.map((track) => (
                <div key={track.id} className="track-result">
                  <div className="track-info">
                    {track.albumArt && (
                      <img src={track.albumArt} alt={track.album} className="track-image" />
                    )}
                    <div>
  <div className="track-name">
    {track.name}
    {track.explicit && (
      <span style={{
        marginLeft: '8px',
        backgroundColor: '#666',
        color: 'white',
        padding: '2px 6px',
        borderRadius: '3px',
        fontSize: '11px',
        fontWeight: 'bold'
      }}>E</span>
    )}
  </div>
  <div className="track-artist">{track.artist}</div>
  <div className="track-meta">
    {track.album} • {formatDuration(track.duration)}
  </div>
</div>
                  </div>
                  <button 
                    className="add-track-btn"
                    onClick={() => handleTrackSelect(track)}
                  >
                    {replacementMode ? 'Select This Song' : 'Add to Playlist'}
                  </button>
                </div>
              ))}
            </div>
          </>
        ) : (
          <div className="player-assignment-form">
            <div className="selected-track-preview">
              {selectedTrack.albumArt && (
                <img src={selectedTrack.albumArt} alt={selectedTrack.album} />
              )}
              <div>
                <div className="track-name">{selectedTrack.name}</div>
                <div className="track-artist">{selectedTrack.artist}</div>
              </div>
            </div>

            <h3>Assign to Player</h3>
            
            {error && <div className="error-message">{error}</div>}

            <form onSubmit={handleAddToPlaylist}>
              <div className="form-group">
                <label>Player Name *</label>
                <input
                  type="text"
                  placeholder="e.g., Sarah Johnson"
                  value={playerName}
                  onChange={(e) => setPlayerName(e.target.value)}
                  required
                  autoFocus
                />
              </div>

              <div className="form-group">
                <label>Player Number</label>
                <input
                  type="number"
                  placeholder="e.g., 8"
                  value={playerNumber}
                  onChange={(e) => setPlayerNumber(e.target.value)}
                  min="0"
                  max="99"
                />
              </div>

              <div className="form-group">
                <label>Clip Selection Method</label>
                <div className="toggle-group">
                  <label className={`toggle-option ${!useAI ? 'active' : ''}`}>
                    <input
                      type="radio"
                      name="selectionMode"
                      checked={!useAI}
                      onChange={() => setUseAI(false)}
                    />
                    <span>Manual Selection</span>
                  </label>
                  <label className={`toggle-option ${useAI ? 'active' : ''}`}>
                    <input
                      type="radio"
                      name="selectionMode"
                      checked={useAI}
                      onChange={() => setUseAI(true)}
                    />
                    <span>🤖 AI Selection</span>
                  </label>
                </div>
              </div>

              {!useAI && (
                <div className="form-group">
                  <label>Start Time (seconds)</label>
                  <input
                    type="number"
                    placeholder="0"
                    value={startTime}
                    onChange={(e) => setStartTime(e.target.value)}
                    min="0"
                  />
                  <small className="field-hint">Where should the song start playing?</small>
                </div>
              )}

              <div className="form-group">
                <label>Duration (seconds)</label>
                <input
                  type="number"
                  placeholder="20"
                  value={duration}
                  onChange={(e) => setDuration(e.target.value)}
                  min="1"
                  max="120"
                />
                <small className="field-hint">
                  {useAI 
                    ? 'AI will find the best segment of this length' 
                    : 'How long should it play?'}
                </small>
              </div>

              {useAI && (
                <div className="ai-info-box">
                  <strong>🤖 AI Mode:</strong> Claude will analyze the song and pick the most energetic {duration}-second segment for maximum crowd impact!
                </div>
              )}

              <div className="form-actions">
                <button 
                  type="button" 
                  className="back-btn"
                  onClick={() => {
                    setSelectedTrack(null);
                    setPlayerName('');
                    setPlayerNumber('');
                    setStartTime('0');
                    setDuration('20');
                    setUseAI(false);
                  }}
                >
                  ← Back to Search
                </button>
                <button type="submit" className="submit-btn" disabled={adding || analyzingAI}>
  {analyzingAI ? (
    <>
      <span className="spinner">⏳</span>
      Analyzing... (30-60 seconds)
    </>
  ) : adding ? 'Adding...' : 'Add to Playlist'}
</button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}

export default SongSearch;